import { css } from '@emotion/react';
import styled from '@emotion/styled';
import React from 'react';
import { breakpoints, colors, spacing } from '../../../../utils/styleguide';
import { ImageStyleProps } from '../../../contentful-elements/Image';
import GhostButton from '../../buttons/GhostButton';
import Typography from '../../text/Typography';
import PropsComponent from '../../types/PropsComponent';

type CardContainerProps = {
  desktopCardWidth: number;
  mobileCardWidth: number;
};

const CardContainer = styled.div`
  display: block;
  width: ${(p: CardContainerProps) => p.mobileCardWidth}px;
  margin: 0 ${spacing[2]}px;
  border-radius: 4px;
  box-sizing: border-box;
  box-shadow: 2px 8px 16px rgba(10, 29, 82, 0.12);
  overflow: hidden;
  text-decoration: none;
  ${breakpoints.tablet} {
    margin: 0 ${spacing[3]}px;
    width: ${(p: CardContainerProps) => p.desktopCardWidth}px;
  }
`;

const Card = styled.div`
  position: relative;
  width: 100%;
  height: ${508}px;
  background: ${colors.white};
  display: flex;
  flex-direction: column-reverse;
  justify-content: flex-end;
  ${breakpoints.tablet} {
    height: ${404}px;
    flex-direction: row;
    justify-content: flex-start;
  }
`;

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  margin: ${spacing[6]}px ${spacing[4]}px ${spacing[4]}px;
  justify-content: space-between;
  height: 100%;
  ${breakpoints.tablet} {
    margin: ${spacing[6]}px;
    width: 100%;
    justify-content: unset;
    height: unset;
  }
`;

const CampaignText = styled(Typography)`
  overflow-wrap: break-word;
  margin-top: ${spacing[3]}px;
  ${breakpoints.tablet} {
    margin-top: ${spacing[4]}px;
  }
`;

const CampaignLink = styled.div`
  overflow-wrap: break-word;
  margin-top: ${spacing[4]}px;
  ${breakpoints.tablet} {
    margin-top: auto;
  }
`;
const UpperCardContent = styled.div``;

const SnapContainer = styled.div`
  scroll-snap-align: start;
  scroll-margin-left: ${spacing[2]}px;
  ${breakpoints.tablet} {
    scroll-margin-left: ${spacing[3]}px;
  }
  &:last-child {
    min-width: 100vw;
  }
`;

export type Campaign = {
  name: string;
  description: string;
  link: string;
  Image: PropsComponent<ImageStyleProps>;
  Logo: PropsComponent<ImageStyleProps>;
};

type CampaignCardProps = Partial<Campaign> & {
  hideShadow?: boolean;
  desktopCardWidth: number;
  mobileCardWidth: number;
};

const CampaignCard = ({
  name,
  description,
  link,
  Image,
  Logo,
  desktopCardWidth,
  mobileCardWidth,
}: CampaignCardProps) => {
  return (
    <SnapContainer>
      <CardContainer desktopCardWidth={desktopCardWidth} mobileCardWidth={mobileCardWidth}>
        <Card>
          <CardContent>
            <UpperCardContent>
              {Logo ? (
                <Logo
                  wrapperCss={css`
                    height: 24px;
                    object-fit: contain;
                    z-index: 1;
                  `}
                  css={css`
                    object-fit: contain;
                    height: 100%;
                  `}
                />
              ) : null}
              <CampaignText variant={'preamble'}>{name}</CampaignText>
              <CampaignText color={colors.darkGrey}>{description}</CampaignText>
            </UpperCardContent>
            <CampaignLink>
              <GhostButton
                href={link}
                label="Read more"
                trailingIconName="external-link"
                hideUnderline
                color={colors.info}
              />
            </CampaignLink>
          </CardContent>
          {Image ? (
            <Image
              wrapperCss={css`
                background: ${colors.black};
                width: 100%;
                height: ${170}px;
                ${breakpoints.tablet} {
                  width: 253px;
                  height: 100%;
                  min-width: 253px;
                  max-width: 253px;
                }
              `}
              css={css`
                object-fit: cover;
                width: 100%;
                height: 100%;
              `}
            />
          ) : null}
        </Card>
      </CardContainer>
    </SnapContainer>
  );
};

const OuterWrapper = styled.div`
  width: 100%;
  box-sizing: border-box;
  display: flex;
  align-items: flex-start;
  scroll-behavior: smooth;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  overflow-x: scroll;
  overflow-y: hidden;
  padding: 0 ${spacing[1]}px ${spacing[4]}px;
  ::-webkit-scrollbar {
    display: none;
  }
  ${breakpoints.tablet} {
    padding: 0 ${spacing[7]}px ${spacing[6]}px;
  }
`;

interface CarouselSliderProps {
  items: Partial<Campaign>[];
  activePostNr: number;
  onScroll: React.EventHandler<React.UIEvent<HTMLElement>>;
  desktopCardWidth: number;
  mobileCardWidth: number;
}

const CarouselSlider = React.forwardRef<HTMLDivElement, CarouselSliderProps>(
  function CarouselSlider(
    { items, activePostNr, onScroll, desktopCardWidth, mobileCardWidth },
    ref,
  ) {
    return (
      <OuterWrapper ref={ref} onScroll={onScroll}>
        {items.map(({ name, description, link, Image, Logo }, index: number) => {
          return (
            <CampaignCard
              key={index}
              name={name}
              description={description}
              link={link}
              Image={Image}
              Logo={Logo}
              hideShadow={activePostNr - 1 === index}
              desktopCardWidth={desktopCardWidth}
              mobileCardWidth={mobileCardWidth}
            />
          );
        })}
      </OuterWrapper>
    );
  },
);

export default CarouselSlider;
